/*
 *  -----------------------------------------------------------------------
 *    <copyright file="assign-crf/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from '@progress/kendo-data-query';
import LibraryGrid from '@components/admin/library-grid';
import { LibraryGridColumn } from '@components/admin/library-grid/library-grid.interface';
import Loading from '@components/core/loading';
import { useBoolean, useGridCells, useTranslation } from 'hooks';

import {
    StudyVisitScheduleFields_StudySubjectVisitScheduleTemplate_visits_StudySubjectVisit as StudyVisitScheduleFields_StudySubjectVisit,
    StudyCrfFields,
} from 'queries/generated/fragments.generated';
import { GET_STUDY_CRFS } from 'queries/study-crfs';
import { GET_STUDY_VISIT_SCHEDULES, SAVE_STUDY_VISIT_SCHEDULE_VISIT } from 'queries/study-visit-schedules';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import NotificationService from '@utils/notification';
import { studyCrfs, studyCrfsVariables } from '@queries/generated/study-crfs.generated';
import {
    saveStudyVisitScheduleVisit,
    saveStudyVisitScheduleVisitVariables,
} from '@queries/generated/study-visit-schedules.generated';

interface AssignCrfDialogInterface {
    open: boolean;
    close: (action?: string, amount?: number) => void;
    currentVisit: StudyVisitScheduleFields_StudySubjectVisit;
    studyId: string;
    currentScheduleId?: string | null | undefined;
}

const AssignCrfDialog = ({ currentVisit, currentScheduleId, studyId, open, close }: AssignCrfDialogInterface) => {
    const { t } = useTranslation();
    const { MaxLengthCell } = useGridCells();

    const [selectedCrfs, setSelectedCrfs] = useState<StudyCrfFields[]>([]);
    const [visitCrfs, setVisitCrfs] = useState<StudyCrfFields[]>([]);
    const [visitCrfIds, setVisitCrfIds] = useState<any[]>([]);
    const saving = useBoolean(false);

    const [loadData, { data, loading }] = useLazyQuery<studyCrfs, studyCrfsVariables>(GET_STUDY_CRFS);
    const [saveVisit] = useMutation<saveStudyVisitScheduleVisit, saveStudyVisitScheduleVisitVariables>(
        SAVE_STUDY_VISIT_SCHEDULE_VISIT,
    );

    useEffect(() => {
        if (studyId) loadData({ variables: { studyId } });
    }, [studyId]);

    useEffect(() => {
        if (currentVisit?.eventIds) setVisitCrfIds(currentVisit?.eventIds);
    }, [currentVisit]);

    const getAvailableCRFs = (): StudyCrfFields[] => {
        const availableCrfs = data?.crfs?.filter(
            (item: StudyCrfFields | null) => item !== null && (visitCrfIds || []).indexOf(item.id) < 0,
        ) as StudyCrfFields[];

        return orderBy(availableCrfs, [{ field: 'crfName', dir: 'asc' }]);
    };

    useEffect(() => {
        if (open && currentVisit && data?.crfs) {
            setVisitCrfs(getAvailableCRFs());
        }
    }, [open, data, visitCrfIds]);

    const onClose = (action?: string, amount?: number) => {
        setSelectedCrfs([]);
        saving.setTrue();
        close(action, amount);
        saving.setFalse();
    };

    const getSubmitData = (): saveStudyVisitScheduleVisitVariables => {
        let ids: any[];
        ids = selectedCrfs.map((r: StudyCrfFields) => r?.id);
        const allIds = visitCrfIds.concat(ids);
        ids = [...Array.from(new Set(allIds))];

        return {
            studyId,
            visitScheduleId: currentScheduleId,
            visit: {
                id: currentVisit.id,
                active: currentVisit.active,
                sequenceNumber: currentVisit.sequenceNumber,
                type: currentVisit.type,
                visitId: currentVisit.visitId,
                visitName: currentVisit.visitName,
                leadVisitId: currentVisit.leadVisitId,
                leadPeriod: currentVisit.leadPeriod,
                leadUnit: currentVisit.leadUnit,
                minWinPeriod: currentVisit.minWinPeriod,
                maxWinPeriod: currentVisit.maxWinPeriod,
                minWinUnit: currentVisit.minWinUnit,
                minOccurrences: currentVisit.minOccurrences,
                forecastEndDateField: currentVisit.forecastEndDateField,
                isEndDateEarliest: currentVisit.isEndDateEarliest,
                eventIds: ids,
            },
        };
    };

    const assignItems = (closeModal?: boolean) => {
        saving.setTrue();

        saveVisit({
            variables: {
                ...getSubmitData(),
            },
            refetchQueries: [
                {
                    query: GET_STUDY_VISIT_SCHEDULES,
                    variables: {
                        studyId,
                    },
                },
            ],
        })
            .then((response: any) => {
                response.data.saveStudyVisitScheduleVisit.visits.forEach((visit: any) => {
                    if (visit.id === currentVisit.id) setVisitCrfIds(visit.eventIds);
                });
                setSelectedCrfs([]);

                const amount = selectedCrfs.length;

                NotificationService.showNotification(
                    t('admin:successfulAssignedCRFsToVisit', {
                        amount,
                        id: currentVisit.recordId || currentVisit.id,
                    }),
                );

                if (closeModal) onClose('success', amount);
            })
            .catch(() => {
                onClose('error');
            })
            .finally(() => {
                saving.setFalse();
            });
    };

    const columns: LibraryGridColumn[] = [
        {
            title: t('admin:formId'),
            id: 'assign-crf-formId',
            field: 'subjectCode',
            cells: { data: MaxLengthCell(30) },
        },
        {
            title: t('admin:name'),
            id: 'assign-crf-crfName',
            field: 'crfName',
            cells: { data: MaxLengthCell(150) },
        },
    ];

    if (loading) return <Loading float />;

    return (
        <CustomDialog
            open={open}
            onClose={() => {
                onClose();
            }}
            fullWidth
            maxWidth="md"
            scroll="paper"
            className="custom-width-popup max-w700"
        >
            <DialogTitle id="assign-crf-modal-title">
                {t('admin:assignCRFs')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        onClose();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-risk-dialog-content pb-0 mb-20">
                <>
                    {currentVisit && (
                        <p className="description mb-20">
                            <span className="title">{t('subject:visitName')}:</span>
                            {currentVisit.visitName}
                        </p>
                    )}
                    <LibraryGrid
                        items={visitCrfs}
                        columns={columns}
                        itemsType={t('admin:crfRecordS')}
                        selection={{
                            selectedItems: selectedCrfs,
                            setSelectedItems: setSelectedCrfs,
                            showItemsCount: true,
                        }}
                    />
                </>
            </DialogContent>
            <DialogActions className="actions-add-risk assign-items flex-wrap pt-0">
                <Button
                    text="ra:assignRisk"
                    disabled={!selectedCrfs.length || saving.value}
                    id="assign-crf-save"
                    onClick={() => assignItems(false)}
                />
                <Button
                    text="ra:assignAndClose"
                    disabled={!selectedCrfs.length || saving.value}
                    id="assign-crf-save-close"
                    onClick={() => assignItems(true)}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AssignCrfDialog;
